import { calculateBlockPosition, calculateBottomLinePosition, calculateLinePosition, calculatePosition, sortPositions } from '@v2/elements/Histogram/helpers';
import { BottomBlock, FormattedBottomBlock, FormattedDataElement, LineChartProps, PositionedDataElement } from '@v2/elements/Histogram/types';

export interface UseManageHistogramParams extends Pick<LineChartProps, 'data' | 'elementWidth' | 'P25' | 'P50' | 'P75' | 'hiddenNullPercentiles'> {}

export const useManageHistogram = ({
  data,
  elementWidth,
  P25 = 0,
  P50 = 0,
  P75 = 0,
  hiddenNullPercentiles = false,
}: UseManageHistogramParams) => {
  const circleSize = 15;

  const topPopupPadding = 5;
  const topPopupMargin = 5;

  const dataLength = data.length;

  const possibleTopPopUpWidth = (elementWidth - (dataLength - 1) * topPopupPadding) / dataLength;
  const topPopUpWidth = Math.min(possibleTopPopUpWidth, 100);

  const bottomPopUpWidth = 100;

  const bottomPopupPadding = 5;
  const bottomPopupMargin = 10;

  const pValues = [P25, P50, P75];
  const filterPValues = pValues.filter((value) => value !== 0);
  const currentPValues = !hiddenNullPercentiles ? pValues : filterPValues;

  const dataValues = data.map((element) => element.value);
  const allValues = [...dataValues, ...currentPValues];

  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);

  const rangeAdjustmentFactor = 8;

  const pMin = Math.min(...currentPValues) - (maxValue - minValue) / rangeAdjustmentFactor;
  const pMax = Math.max(...currentPValues) + (maxValue - minValue) / rangeAdjustmentFactor;

  const minimum = Math.min(minValue, pMin, pMax);
  const maximum = Math.max(maxValue, pMin, pMax);

  // Typed positions array
  const positions: PositionedDataElement[] = data.map((element) => ({
    ...element,
    position: calculatePosition(element.value, minimum, maximum, elementWidth),
    icon: element.icon,
  }));

  const sortedPositions: PositionedDataElement[] = sortPositions(positions);

  // Calculate formatted positions for each element
  let cumulativeOffset = 0;
  const formattedPositions: FormattedDataElement[] = [];
  const numberOfElements = sortedPositions.length;

  sortedPositions.forEach((element, index) => {
    const remainingElements = numberOfElements - index;
    const limit = elementWidth - (topPopUpWidth + (index === 0 ? 0 : topPopupMargin)) * remainingElements + topPopupMargin;

    const formattedPosition = calculateBlockPosition(element.position, cumulativeOffset, limit, topPopUpWidth);

    formattedPositions.push({
      ...element,
      formattedPosition,
      zIndex: index * 10 + 1,
    });

    cumulativeOffset = formattedPosition + topPopUpWidth + topPopupPadding;
  });

  // Calculate line positions for each element
  const lineOffsetFactor = topPopUpWidth / 2;
  const topLines = formattedPositions.map((element) => ({
    line: calculateLinePosition(element.position, element.formattedPosition + lineOffsetFactor),
    element,
    zIndex: element.zIndex,
  }));

  // Calculate positions for P25, P50, P75
  const P25Position = calculatePosition(P25, minimum, maximum, elementWidth);
  const P50Position = calculatePosition(P50, minimum, maximum, elementWidth);
  const P75Position = calculatePosition(P75, minimum, maximum, elementWidth);

  // Typed bottomBlocks array
  const bottomBlocks: BottomBlock[] = [
    {
      name: 'p25',
      position: P25Position,
      seconds: P25,
    },
    {
      name: 'p50',
      position: P50Position,
      seconds: P50,
    },
    {
      name: 'p75',
      position: P75Position,
      seconds: P75,
    },
  ];

  // Calculate formatted positions and lines for bottom blocks
  let cumulativeBottomOffset = 0;
  const formattedBottomBlocks: FormattedBottomBlock[] = [];
  const numberOfBottomBlocks = bottomBlocks.length;

  bottomBlocks.forEach((block, index) => {
    const remainingElements = numberOfBottomBlocks - index;
    const limit = elementWidth - bottomPopUpWidth * remainingElements - bottomPopupMargin;
    const formattedPosition = calculateBlockPosition(block.position, cumulativeBottomOffset, limit, bottomPopUpWidth);

    const line = calculateBottomLinePosition(block.position, formattedPosition, bottomPopUpWidth);

    formattedBottomBlocks.push({
      ...block,
      formattedPosition,
      ...line,
      zIndex: 10,
    });

    cumulativeBottomOffset = formattedPosition + bottomPopUpWidth + bottomPopupPadding;
  });

  return {
    circleSize,
    topPopUpWidth,
    bottomPopUpWidth,
    formattedPositions,
    topLines,
    P25Position,
    P75Position,
    formattedBottomBlocks,
  };
};
